import React, { useEffect, useRef, useState } from 'react';
import { regulatoryApprovals } from '../data/mockData';
import { useTranslation } from 'react-i18next';

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const RegulatoryApprovals = () => {
  const scrollRef = useRef(null);
  const [sectionRef, visible] = useScrollReveal();
  const tripleApprovals = [...regulatoryApprovals, ...regulatoryApprovals, ...regulatoryApprovals];
  const { t } = useTranslation();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let amount = 0;
    const id = setInterval(() => {
      amount += 0.8; el.scrollLeft = amount;
      if (amount >= el.scrollWidth / 2) amount = 0;
    }, 20);
    return () => clearInterval(id);
  }, []);

  return (
    <section ref={sectionRef} className="reg-section">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&display=swap');

        @keyframes regPulse { 0%,100% { box-shadow: 0 4px 20px rgba(58,143,163,0.08); } 50% { box-shadow: 0 8px 32px rgba(58,143,163,0.22); } }

        /* ── Background untouched ── */
        .reg-section {
          font-family: 'Outfit', sans-serif; padding: 80px 0;
          background: linear-gradient(180deg, #f8faff 0%, #fff 100%);
          position: relative; overflow: hidden;
        }
        .reg-bg-pattern {
          position: absolute; inset: 0;
          background-image: radial-gradient(circle, rgba(58,143,163,0.05) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        .reg-inner { max-width: 1280px; margin: 0 auto; padding: 0 24px; position: relative; }

        .reg-header { text-align: center; margin-bottom: 48px; opacity: 0; transform: translateY(30px); transition: all 0.7s cubic-bezier(0.4,0,0.2,1); }
        .reg-header.in { opacity: 1; transform: translateY(0); }

        /* Eyebrow — logo blue */
        .reg-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #3a8fa3;
          background: rgba(58,143,163,0.08);
          border: 1px solid rgba(58,143,163,0.22);
          padding: 5px 16px; border-radius: 999px; margin-bottom: 14px;
        }
        .reg-title { font-size: clamp(1.8rem, 3.5vw, 2.6rem); font-weight: 700; color: #1a3a4a; margin: 0; }

        .reg-strip { position: relative; overflow: visible; opacity: 0; transform: translateY(20px); transition: all 0.7s cubic-bezier(0.4,0,0.2,1) 0.2s; }
        .reg-strip.in { opacity: 1; transform: translateY(0); }
        /* Fades — background matched */
        .reg-fade-l { position: absolute; left: 0; top: 0; bottom: 0; width: 120px; background: linear-gradient(90deg, #f8faff 0%, transparent 100%); z-index: 2; pointer-events: none; }
        .reg-fade-r { position: absolute; right: 0; top: 0; bottom: 0; width: 120px; background: linear-gradient(270deg, #f8faff 0%, transparent 100%); z-index: 2; pointer-events: none; }
        .reg-scroll { display: flex; gap: 20px; overflow-x: hidden; padding: 24px 0; }

        /* Glass reg cards */
        .reg-card {
          flex-shrink: 0; width: 130px; height: 130px;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.72);
          backdrop-filter: blur(18px) saturate(1.5);
          -webkit-backdrop-filter: blur(18px) saturate(1.5);
          border: 1px solid rgba(255,255,255,0.88);
          border-radius: 20px; padding: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06), 0 1px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.95);
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s, border-color 0.3s;
          cursor: pointer; position: relative; overflow: hidden;
        }
        /* Top accent — logo blue */
        .reg-card::before {
          content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 2px;
          background: linear-gradient(90deg, transparent, #3a8fa3, #5aab9a, transparent);
          opacity: 0; transition: opacity 0.3s;
        }
        /* Hover — scale up card */
        .reg-card {
          transition: transform 0.35s cubic-bezier(0.4,0,0.2,1), box-shadow 0.35s, border-color 0.3s, background 0.3s;
        }
        .reg-card:hover {
          transform: translateY(-12px) scale(1.28);
          box-shadow: 0 20px 48px rgba(58,143,163,0.28), 0 6px 16px rgba(0,0,0,0.10);
          border-color: rgba(58,143,163,0.4);
          background: rgba(255,255,255,0.95);
          z-index: 10;
          animation: regPulse 1.5s ease-in-out;
        }
        .reg-card:hover::before { opacity: 1; }
        .reg-card img {
          width: 64px; height: 64px; object-fit: contain; margin-bottom: 8px;
          transition: width 0.3s, height 0.3s;
        }
        .reg-card:hover img { width: 76px; height: 76px; }
        .reg-card p {
          font-size: 0.72rem; text-align: center; color: #64748b;
          font-weight: 500; margin: 0;
          transition: font-size 0.3s, color 0.3s;
        }
        .reg-card:hover p { font-size: 0.8rem; color: #3a8fa3; font-weight: 600; }
        /* give strip overflow visible so scaled card isn't clipped */
        .reg-scroll { overflow-x: hidden; padding: 24px 0; }
      `}</style>

      <div className="reg-bg-pattern" />
      <div className="reg-inner">
        <div className={`reg-header ${visible ? 'in' : ''}`}>
          <div className="reg-eyebrow">{t("regulatorysection.eyebrow")}</div>
          <h2 className="reg-title">{t("regulatorysection.title")}</h2>
        </div>
        <div className={`reg-strip ${visible ? 'in' : ''}`}>
          <div className="reg-fade-l" /><div className="reg-fade-r" />
          <div ref={scrollRef} className="reg-scroll" style={{ scrollBehavior: 'auto' }}>
            {tripleApprovals.map((item, i) => (
              <div key={i} className="reg-card">
                <img src={item.logo} alt={item.name} />
                <p>{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegulatoryApprovals;